/**
 * TroubleshootTab — MQTT debug logging control + debug data table.
 *
 * Visible only for users with roleWeight >= 80.
 * Two sections:
 *   1. "Device Logging" — duration selector + Start Logging button
 *   2. "Debug Data" — parsed DynamoDB byte data table
 */

import { useState, useCallback } from 'react';
import { getLocalTimeFromEpochMillis } from '../../../../libs.jsx';

const TroubleshootTab = ({ debugData, serialNum, tzOffset, onStartLogging, showToast }) => {
  const [duration, setDuration] = useState('600');
  const [starting, setStarting] = useState(false);

  const handleStartLogging = useCallback(async () => {
    setStarting(true);
    await onStartLogging(duration);
    setStarting(false);
  }, [duration, onStartLogging]);

  const copyByteString = useCallback((byteString) => {
    navigator?.clipboard?.writeText(byteString);
    showToast('success', 'Byte string copied to clipboard.');
  }, [showToast]);

  return (
    <>
      {/* Device Logging Control */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Device Logging</h3>
        </div>
        <div className="pp-section__body">
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
            Start capturing device debug data for troubleshooting. Will clear and restart if logging is already running.
          </p>
          <div className="ip-logging-control">
            <div className="ip-logging-control__field">
              <span className="ip-logging-control__label">Duration</span>
              <select
                className="dp-select"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                style={{ minWidth: '160px' }}
              >
                <option value="600">10 Minutes</option>
                <option value="1800">30 Minutes</option>
                <option value="3600">1 Hour</option>
                <option value="10800">3 Hours</option>
                <option value="21600">6 Hours</option>
                <option value="86400">24 Hours</option>
              </select>
            </div>
            <button
              className="dp-btn dp-btn--primary dp-btn--sm"
              onClick={handleStartLogging}
              disabled={starting || !serialNum}
              title="Starts logging device debug data for troubleshooting"
            >
              {starting ? 'Starting…' : 'Start Logging'}
            </button>
          </div>
        </div>
      </div>

      {/* Debug Data Table */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Debug Data</h3>
          <div className="pp-section__actions">
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
              {debugData?.length || 0} logs
            </span>
          </div>
        </div>
        <div className="pp-section__body pp-section__body--flush">
          {debugData?.length > 0 ? (
            <div className="dp-table-wrap">
              <table className="dp-table ip-debug-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">Timestamp</th>
                    <th className="dp-th">Raw Bytes</th>
                    <th className="dp-th">Alarm 0</th>
                    <th className="dp-th">Alarm 1</th>
                    <th className="dp-th">No Change</th>
                    <th className="dp-th">Reset</th>
                    <th className="dp-th">Stat 1</th>
                    <th className="dp-th">Stat 2</th>
                    <th className="dp-th">Supervision</th>
                    <th className="dp-th">Hop / Count</th>
                    <th className="dp-th">Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {debugData.map((item, idx) => (
                    <tr key={idx} className="dp-tr">
                      <td className="dp-td">
                        <span className="dp-cell-text" style={{ whiteSpace: 'nowrap' }}>
                          {item.EventTime ? getLocalTimeFromEpochMillis(item.EventTime, tzOffset) : '—'}
                        </span>
                      </td>
                      <td className="dp-td">
                        <button
                          className="ip-raw-bytes-btn"
                          onClick={() => copyByteString(item.ByteString || '')}
                          title={item.ByteString || 'No data'}
                        >
                          RAW
                        </button>
                      </td>
                      <td className="dp-td">{item.Parsed?.alarm0 != null ? item.Parsed.alarm0.toString() : 'N/A'}</td>
                      <td className="dp-td">{item.Parsed?.alarm1 != null ? item.Parsed.alarm1.toString() : 'N/A'}</td>
                      <td className="dp-td">{item.Parsed?.noChange != null ? item.Parsed.noChange.toString() : 'N/A'}</td>
                      <td className="dp-td">{item.Parsed?.reset != null ? item.Parsed.reset.toString() : 'N/A'}</td>
                      <td className="dp-td">{item.Parsed?.stat1 != null ? item.Parsed.stat1.toString() : 'N/A'}</td>
                      <td className="dp-td">{item.Parsed?.stat2 != null ? item.Parsed.stat2.toString() : 'N/A'}</td>
                      <td className="dp-td">{item.Parsed?.supervision != null ? item.Parsed.supervision.toString() : 'N/A'}</td>
                      <td className="dp-td">
                        <span>{item.Parsed?.uuidFirstHop != null ? item.Parsed.uuidFirstHop : 'N/A'}</span>
                        <br />
                        <span style={{ fontSize: '0.6rem', color: 'var(--color-text-tertiary)' }}>
                          {item.Parsed?.hopCount != null ? item.Parsed.hopCount : 'N/A'}
                        </span>
                      </td>
                      <td className="dp-td">{item.Parsed?.signal != null ? item.Parsed.signal : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="dp-empty" style={{ padding: 'var(--space-4)' }}>
              <p className="dp-empty__title">No debug logs</p>
              <p className="dp-empty__text">Start logging to capture device debug data.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TroubleshootTab;
